import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import interiorBC from '../assets/Interior_BC.jpg';

const InteriorBCPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Vegetation Management Interior BC | Boultbee</title>
            <meta name="description" content="Herbicide application services in Kamloops, Merritt, Revelstoke, and Interior BC. Railroad, utility, municipal, and industrial vegetation management." />
            <link rel="canonical" href="https://www.weedmanager.com/service-areas/interior-bc" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Vegetation Management Interior BC | Boultbee" />
            <meta property="og:description" content="Herbicide application services in Kamloops, Merritt, Revelstoke, and Interior BC. Railroad, utility, municipal, and industrial vegetation management." />
            <meta property="og:url" content="https://www.weedmanager.com/service-areas/interior-bc" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-interior.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Vegetation Management Interior BC | Boultbee" />
            <meta name="twitter:description" content="Herbicide application services in Kamloops, Merritt, Revelstoke, and Interior BC. Railroad, utility, municipal, and industrial vegetation management." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-interior.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Interior BC & Cariboo", "item": "https://www.weedmanager.com/service-areas/interior-bc" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Industrial Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": [
                  { "@type": "City", "name": "Kamloops" },
                  { "@type": "City", "name": "Merritt" },
                  { "@type": "City", "name": "Revelstoke" },
                  { "@type": "City", "name": "Salmon Arm" },
                  { "@type": "City", "name": "Golden" },
                  { "@type": "City", "name": "Chase" },
                  { "@type": "City", "name": "Sicamous" }
                ],
                "description": "Industrial and municipal herbicide application across Interior BC including Kamloops, Merritt, Revelstoke, and the Thompson-Nicola region."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Interior BC & Cariboo"
            subtitle="Herbicide application from Kamloops through the Thompson-Nicola to Revelstoke"
            backgroundImage={interiorBC}
            bgPosition="center bottom"
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Boultbee deploys experienced crews to Interior communities with the equipment and expertise each site demands.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Industrial & Infrastructure</h2>
              <p className="text-gray-700">The BC Interior has significant vegetation management demand along rail corridors, industrial sites, and utility infrastructure. Boultbee provides hi-rail herbicide application for ballast weed control, yard vegetation, and right-of-way management. We also service highway infrastructure including rest areas, median vegetation, and guardrail zones along major Interior highways.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Industrial & Resource Sector</h2>
              <p className="text-gray-700">Interior BC's resource-based economy generates significant vegetation management demand across pulp mills, mining operations, and pipeline infrastructure throughout the Thompson-Nicola and Cariboo regions. Our bare ground programs maintain vegetation-free conditions around mill yards, log sorts, mine sites, and industrial compounds.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Municipal & Utility Services</h2>
              <p className="text-gray-700">Interior BC municipalities rely on professional vegetation management for sidewalk weed control, park turf programs, and noxious weed compliance. We also maintain bare ground conditions at power and gas utility substations throughout the Interior region.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Service Areas */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Regions We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/service-areas/lower-mainland" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Lower Mainland</h3>
                  <p className="text-gray-700 text-sm mt-1">Vancouver, Surrey & Fraser Valley</p>
                </Link>
                <Link to="/service-areas/okanagan" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Okanagan</h3>
                  <p className="text-gray-700 text-sm mt-1">Penticton, Kelowna & Vernon</p>
                </Link>
                <Link to="/service-areas/northern-bc" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Northern BC</h3>
                  <p className="text-gray-700 text-sm mt-1">Prince George & Central Interior</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">Serving Interior communities from our Okanagan base. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us for a site assessment.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default InteriorBCPage;
